import { Modal, Tabs, TabsList, TabsTrigger, toast } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { AlertCircleIcon, Building2Icon, CoinsIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  FeeRuleCreateInput,
  FeeRuleUpdateInput,
  FeeType,
} from "@/modules/fees/domain/contracts";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import type { FeeRuleDTO } from "../api/client";
import { feesApi } from "../api/client";
import {
  feesKeys,
  useCreateFeeRule,
  useDeleteFeeRule,
  useFeeRulesQuery,
  useUpdateFeeRule,
} from "../api/queries";
import { CategoryFeeView } from "../ui/CategoryFeeView";
import { FeeRuleForm } from "../ui/FeeRuleForm";
import { FeeRulesTable } from "../ui/FeeRulesTable";
import { FeesFilter } from "../ui/FeesFilter";
import { FeesToolbar } from "../ui/FeesToolbar";
import { ShopFeeView } from "../ui/ShopFeeView";

type FeeTabValue = FeeType | "by_category" | "by_shop";

const FEE_TABS: { value: FeeTabValue; label: string }[] = [
  { value: "marketplace_commission", label: "ຄ່າຄອມມິດຊັນ Marketplace" },
  { value: "mechanic_commission", label: "ຄ່າທຳນຽມງານຊ່ງ" },
  { value: "by_category", label: "ຕາມຫມວດຫມູ່" },
  { value: "by_shop", label: "ຕາມຮ້ານ (Override)" },
];

type FeesSearch = {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  filters?: FilterConditionDTO[];
  q?: string;
  shopId?: string;
};

export function FeesPage() {
  const nav = useNavigate({ from: "/app/fees" });
  const search = useSearch({ from: "/app/fees" }) as FeesSearch & {
    tab?: FeeTabValue;
  };

  const tab = search.tab ?? "marketplace_commission";
  const feeType =
    tab === "by_category" || tab === "by_shop"
      ? "marketplace_commission"
      : (tab as FeeType);

  const list = useFeeRulesQuery(feeType, {
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
    sort: search.sort,
    filters: search.filters,
    q: search.q,
  });

  const [feeFormOpen, setFeeFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<FeeRuleDTO | null>(null);
  const [defaultScopeCategoryId, setDefaultScopeCategoryId] = useState<
    string | null
  >(null);
  const [defaultScopeShopId, setDefaultScopeShopId] = useState<string | null>(
    null,
  );

  const queryClient = useQueryClient();
  const deleteRule = useDeleteFeeRule();
  const createRule = useCreateFeeRule();
  const updateRule = useUpdateFeeRule();
  const canCreate = useActionPermission([Permissions.fee_rules.create]);
  const canManage = useActionPermission([Permissions.fee_rules.update], "any");

  const data = list.data?.data ?? [];
  const meta = list.data?.meta ?? { total: 0, limit: 20, offset: 0 };

  const isEdit = editingRule != null;

  const setTab = (value: string) => {
    nav({
      search: {
        ...search,
        tab: value as FeeTabValue,
        offset: 0,
      },
    });
  };

  useEffect(() => {
    if (tab === "by_shop" && search.shopId) {
      setDefaultScopeShopId(search.shopId);
      setFeeFormOpen(true);
      nav({ search: { ...search, shopId: undefined } });
    }
  }, [tab, search.shopId]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄ່າທຳນຽມ" },
          ]}
        />
      </Header>

      <Main>
        <FeesToolbar
          onAddRule={() => {
            setEditingRule(null);
            setFeeFormOpen(true);
          }}
          canCreate={canCreate}
        />

        <Modal
          open={feeFormOpen}
          onOpenChange={(open) => {
            setFeeFormOpen(open);
            if (!open) {
              setEditingRule(null);
              setDefaultScopeCategoryId(null);
              setDefaultScopeShopId(null);
            }
          }}
          title={isEdit ? "ແກ້ໄຂກົດຄ່າທຳນຽມ" : "ສ້າງກົດຄ່າທຳນຽມ"}
          description={
            isEdit
              ? "ແກ້ໄຂລາຍລະອຽດກົດຄ່າທຳນຽມ"
              : "ກຳນົດຂອບເຂດ ປະເພດຄ່າ ແລະ ຄ່າທີ່ຕ້ອງການ"
          }
          size="lg"
          className={[
            "flex max-h-[min(90dvh,760px)] flex-col gap-4 overflow-hidden",
            "[&>[data-slot=dialog-header]]:shrink-0",
          ].join(" ")}
        >
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
            <FeeRuleForm
            mode={isEdit ? "edit" : "create"}
            initialValues={
              editingRule
                ? {
                    scope: editingRule.scope,
                    scopeId: editingRule.scopeId ?? null,
                    feeType: editingRule.feeType as
                      | "marketplace_commission"
                      | "mechanic_commission",
                    feeMode: editingRule.feeMode as "percent" | "flat",
                    value: Number(editingRule.value),
                    currency: (editingRule.currency ?? "LAK") as
                      | "LAK"
                      | "THB"
                      | "USD",
                    isActive: editingRule.isActive,
                  }
                : defaultScopeCategoryId
                  ? {
                      scope: "category" as const,
                      scopeId: defaultScopeCategoryId,
                      feeType: "marketplace_commission" as const,
                      feeMode: "percent" as const,
                      value: 0,
                      currency: "LAK" as const,
                      isActive: true,
                    }
                  : defaultScopeShopId
                    ? {
                        scope: "shop" as const,
                        scopeId: defaultScopeShopId,
                        feeType: "marketplace_commission" as const,
                        feeMode: "percent" as const,
                        value: 0,
                        currency: "LAK" as const,
                        isActive: true,
                      }
                    : undefined
            }
            submitting={createRule.isPending || updateRule.isPending}
            onCancel={() => setFeeFormOpen(false)}
            onSubmit={async (vals) => {
              try {
                if (isEdit && editingRule) {
                  await feesApi.update(
                    editingRule.id,
                    vals as FeeRuleUpdateInput,
                  );
                  toast.success("ແກ້ໄຂກົດຄ່າທຳນຽມສຳເລັດ");
                } else {
                  await feesApi.create(vals as FeeRuleCreateInput);
                  toast.success("ເພີ່ມກົດຄ່າທຳນຽມສຳເລັດ");
                }
                setFeeFormOpen(false);
                setEditingRule(null);
                setDefaultScopeCategoryId(null);
                setDefaultScopeShopId(null);
                await queryClient.invalidateQueries({ queryKey: feesKeys.all });
                list.refetch();
              } catch {
                toast.error(isEdit ? "ແກ້ໄຂລົ້ມເຫຼວ" : "ເພີ່ມກົດລົ້ມເຫຼວ");
              }
            }}
            />
          </div>
        </Modal>

        <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SummaryCard
            title="ເຫດຜົນລຳດັບການນຳໃຊ້ກົດ"
            icon={AlertCircleIcon}
            color="orange"
            items={[
              {
                label: "",
                value: undefined,
                children: (
                  <p className="text-muted-foreground text-sm">
                    ກົດທີ່ເຈາະຈົງວ່າ ຈະຖືກນຳໃຊ້ກ່ອນ: ຕໍ່ຮ້ານ ຫຼື ຕໍ່ຊ່ງ → ຕໍ່ຫມວດຫມູ່ ຫຼື ພາກພື້ນ →
                    ທົ່ວແຜງ. ລຳດັບປະມວນ: ເລີ່ມຈາກເທິງລົງ ເລືອກກົດທີ່ກົງກັບທຸລະກຳທີ່ໃກ້ທີ່ສຸດ.
                  </p>
                ),
              },
            ]}
          />
          <SummaryCard
            title="ການຄິດໄລ່ຄ່າທຳນຽມ"
            icon={Building2Icon}
            color="muted"
            items={[
              {
                label: "",
                value: undefined,
                children: (
                  <p className="text-muted-foreground text-sm">
                    ຄ່າຄົງທີ່ໃຊ້ໃນສະກຸນເງິນຂອງທຸລະກຳ. ຄ່າເປີເຊັນຄິດຈາກມູນຄ່າລວມກ່ອນພາສີ.
                  </p>
                ),
              },
            ]}
          />
          <SummaryCard
            title="ຈຳນວນກົດທັງໝົດ"
            icon={CoinsIcon}
            color="primary"
            items={[{ label: "ຈຳນວນກົດທັງໝົດ", value: String(meta.total) }]}
          />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mb-4">
          <TabsList className="w-full">
            {FEE_TABS.map(({ value, label }) => (
              <TabsTrigger className="w-full flex-1" key={value} value={value}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {tab === "by_category" ? (
          <div className="flex flex-col rounded-xl border bg-card pt-2">
            <p className="mb-2 px-2 text-muted-foreground text-sm">
              ກົດຕາມຫມວດຫມູ່ ຈະນຳໃຊ້ກ່ອນກົດທົ່ວແຜງ ເມື່ອລາຍການສິນຄ້າ ຫຼື ງານບໍລິການຢູ່ໃນຫມວດຫມູ່ນັ້ນ.
              ປິດໃຊ້ງານຈະໃຫ້ໃຊ້ກົດທົ່ວແຜງແທນ.
            </p>
            <CategoryFeeView
              canManage={!!canManage}
              offset={search.offset ?? 0}
              limit={search.limit ?? 20}
              onEdit={(row) => {
                setEditingRule(row);
                setDefaultScopeCategoryId(null);
                setDefaultScopeShopId(null);
                setFeeFormOpen(true);
              }}
              onAddForCategory={(categoryId) => {
                setEditingRule(null);
                setDefaultScopeCategoryId(categoryId);
                setDefaultScopeShopId(null);
                setFeeFormOpen(true);
              }}
              onPaginationChange={(offset, limit) =>
                nav({ search: { ...search, offset, limit } })
              }
            />
          </div>
        ) : tab === "by_shop" ? (
          <div className="flex flex-col rounded-xl border bg-card pt-2">
            <p className="mb-2 px-2 text-muted-foreground text-sm">
              ກົດຕໍ່ຮ້ານ (Override) ຈະນຳໃຊ້ກ່ອນກົດທົ່ວແຜງ ຫຼື ຕາມຫມວດຫມູ່.
              ປິດໃຊ້ງານຈະໃຫ້ຮ້ານນັ້ນໃຊ້ກົດລະດັບ platform ແທນ.
            </p>
            <ShopFeeView
              canManage={!!canManage}
              offset={search.offset ?? 0}
              limit={search.limit ?? 20}
              onEdit={(row) => {
                setEditingRule(row);
                setDefaultScopeCategoryId(null);
                setDefaultScopeShopId(null);
                setFeeFormOpen(true);
              }}
              onAddForShop={(shopId) => {
                setEditingRule(null);
                setDefaultScopeCategoryId(null);
                setDefaultScopeShopId(shopId);
                setFeeFormOpen(true);
              }}
              onPaginationChange={(offset, limit) =>
                nav({ search: { ...search, offset, limit } })
              }
            />
          </div>
        ) : (
          <div className="flex flex-col rounded-xl border bg-card pt-2">
            <FeesFilter />

            <FeeRulesTable
              canManage={!!canManage}
              isLoading={list.isLoading}
              data={data}
              offset={meta.offset}
              limit={meta.limit}
              totalCount={meta.total}
              sortBy={search.sort?.[0]?.field}
              sortOrder={search.sort?.[0]?.dir}
              onEdit={(row) => {
                setEditingRule(row);
                setFeeFormOpen(true);
              }}
              onDelete={async (id) => {
                toast.promise(deleteRule.run(id), {
                  loading: "ກຳລັງລຶບ...",
                  success: "ລຶບກົດຄ່າທຳນຽມສຳເລັດ",
                  error: "ລຶບລົ້ມເຫຼວ",
                });
              }}
              onPaginationChange={(offset, limit) =>
                nav({ search: { ...search, offset, limit } })
              }
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    offset: 0,
                    sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                  },
                })
              }
            />
          </div>
        )}
      </Main>
    </>
  );
}
